import { XMLParser } from 'fast-xml-parser'
import $ from 'jquery'
import get from 'lodash/get'
import has from 'lodash/has'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import memoize from 'lodash/memoize'
import OLLayerVector from 'ol/layer/Vector'
import OLStyleCircle from 'ol/style/Circle'
import OLStyleFill from 'ol/style/Fill'
import OLStyleIcon from 'ol/style/Icon'
import OLStyleRegularShape from 'ol/style/RegularShape'
import OLStyleStroke from 'ol/style/Stroke'
import OLStyleStyle from 'ol/style/Style'
import OLStyleText from 'ol/style/Text'

import PoziAPIData from 'api/data'
import * as Model from 'api/model'
import * as PoziUtils from 'api/utils'
import PoziStorageAWS from 'aws/storage'
import ApplyTransformers from 'config/ApplyTransformers'
import LoaderAnnotation from 'config/LoaderAnnotation'
import LoaderCKANAPI from 'config/LoaderCKANAPI'
import LoaderCookie from 'config/LoaderCookie'
import LoaderGeneric from 'config/LoaderGeneric'
import LoaderGML from 'config/LoaderGML'
import LoaderGSheet from 'config/LoaderGSheet'
import LoaderNone from 'config/LoaderNone'
import LoaderOverpass from 'config/LoaderOverpass'
import GeoJSONIO from 'data/geojsonio'
import PoziJotForm from 'data/jotform'
import { setLayerLoadDone, setLayerLoadInProgress, setLayerStyleGeoStyler } from 'drawer/tabs/layers/actions'
import PoziMap from 'map/map'
import PoziEvents from 'utils/events'

import LoaderFlatGeobuf from './LoaderFlatGeobuf'
import RefreshFeatures from './RefreshFeatures'

/**
 * Define config for GeoJSON layers
 */

// A (Adapter)-> PoziConfigGeoJSON function is implemented in order to follow the standard Adapter pattern
const PoziConfigGeoJSON = memoize(function () {
  // Adapters
  const adapters = {
    getGeoJSONLayer: function (layer) {
      return getGeoJSONLayer(layer)
    },
    refreshFeatures: function (geojson, projection, geojsonSource, layer) {
      return RefreshFeatures(geojson, projection, geojsonSource, layer)
    },
    applyTransformers: function (layer, geojson) {
      return ApplyTransformers(layer, geojson)
    }
  }

  const app = PoziUtils.getApp()

  var getGeoJSONLayer = function (layerConf) {
    const configObj = {}
    configObj.pointStyle = null

    if (has(layerConf, 'styleGeoStyler') && has(layerConf, 'styleGeoStyler.urldefault')) {
      // skip this
    } else if (!has(layerConf, 'style') && !has(layerConf, 'styleClass') && !has(layerConf, 'styleClassPolygon')) {
      // Can't find a style, so set to styleGeoJSONIO
      configObj.pointStyle = GeoJSONIO().getStyleFunction(layerConf)
      configObj.pointSelectedStyle = GeoJSONIO().getStyleFunctionSelected(layerConf)
    } else if (layerConf.styleClassPolygon) {
      setPointStyleFunctionForPolygonClass(configObj, layerConf)
    } else if (layerConf.styleGeoJSONIO) {
      // Defines the style of the all feature types (despite the name)
      configObj.pointStyle = GeoJSONIO().getStyleFunction(layerConf)
      configObj.pointSelectedStyle = GeoJSONIO().getStyleFunctionSelected(layerConf)
    } else if (has(layerConf, 'style.mapbox')) { // Temporarly set it to GeoJSONIO... this gets ultimately set when the dataset is loaded in SetMapboxStyle.js
      configObj.pointStyle = function () { return [] } // GeoJSONIO().getStyleFunction(layer)
      configObj.pointSelectedStyle = function () { return [] } // GeoJSONIO().getStyleFunctionSelected(layer)
    } else {
      setPointStyleFunctionForOtherStyleTypes(configObj, layerConf) // Should this be set to GeoJSONIO by default?
    }

    layerConf.config.spatial.urlBase = layerConf.config.spatial.url

    // Replace <POLYGON> in CQL_FILTER
    const cqlFilter = get(layerConf, 'config.spatial.params.CQL_FILTER', '')
    const polygon = Model.get('config.layout.map.views.polygon', '')
    if (cqlFilter !== '' && polygon !== '') {
      layerConf.config.spatial.params.CQL_FILTER = layerConf.config.spatial.params.CQL_FILTER.replace(/<POLYGON>/g, polygon)
    }

    let geojsonSource

    // If there is a loader type in the spatial URL define our own Vector source loader
    const loaderType = get(layerConf, 'config.spatial.loader', null)
    // console.debug('geojson loader type', layerConf.titleid, loaderType)
    if (isNil(loaderType) || loaderType === 'geojson') {
      geojsonSource = LoaderGeneric(layerConf)
    } else {
      // NOTE: any loader added here may need to be added to Download.js as well!
      if (loaderType === 'none') {
        geojsonSource = LoaderNone() // This is used when you want to avoid loading the data using Openlayers.
      } else if (loaderType === 'wmsgetfeatureinfogeoserver') {
        geojsonSource = LoaderNone() // This is used when you want to avoid loading the data using Openlayers.
      } else if (loaderType === 'annotation') {
        geojsonSource = LoaderAnnotation(layerConf)
      } else if (loaderType === 'ckanapi') {
        geojsonSource = LoaderCKANAPI(layerConf)
      } else if (loaderType === 'flatgeobuf') {
        geojsonSource = LoaderFlatGeobuf(layerConf)
      } else if (loaderType === 'gml') {
        geojsonSource = LoaderGML(layerConf)
      } else if (loaderType === 'gsheet') {
        geojsonSource = LoaderGSheet(layerConf)
      } else if (loaderType === 'jotform') {
        geojsonSource = PoziJotForm().loader(layerConf)
      } else if (loaderType === 'awss3') {
        geojsonSource = PoziStorageAWS().loader(layerConf)
      } else if (loaderType === 'cookie') {
        geojsonSource = LoaderCookie(layerConf)
      } else if (loaderType === 'overpass') {
        geojsonSource = LoaderOverpass(layerConf)
      } else if (loaderType === 'hereaddress') {
        // This type of layer is never loaded as a full layer. It requires a geometry target. At this time it is used as a child record. See ChildPanelLayerItems.js for an example.
        geojsonSource = LoaderNone()
      } else {
        console.error('Dataset has a loader but it is not supported!', loaderType)
      }
    }

    if (isNil(geojsonSource)) {
      console.error('Invalid data source.')
      return
    }

    // Allow for direct styling using OL's "flat styles" or fallback to other methods
    const style = layerConf.olStyle || configObj.pointStyle

    const olLayer = new OLLayerVector({
      title: layerConf.title,
      titleid: layerConf.titleid,
      crossOrigin: 'Anonymous',
      group: layerConf.group,
      maxResolution: layerConf.maxResolution || 100000,
      minResolution: layerConf.minResolution || 0.0001,
      opacity: layerConf.opacity || 1,
      style,
      initialised: false, // custom Pozi property
      visible: false
    })
    PoziUtils.setSourceLoaded(olLayer, geojsonSource, layerConf)

    // A bit of a convoluted way to detect the first time the layer is visible to
    // trigger the loading of the layers. This makes sure that the layer only
    // gets loaded when needed (avoids unneccessary data fetching)
    const onLayerFirstTimeVisible = async () => {
      const titleid = olLayer.get('titleid')

      const isVisible = olLayer.getVisible()
      const isInitialised = olLayer.get('initialised', false)

      console.debug('PoziConfigGeoJSON - onLayerFirstTimeVisible', titleid, loaderType, isVisible, isInitialised)

      if (isInitialised) {
        console.warn('PoziConfigGeoJSON - Layer already initialised. We should never have gotten here.', titleid)
        return
      }

      if (isVisible) {
        // Add dynamically loaded GeoStyler styles before loading the data
        if (!isEmpty(layerConf?.styleGeoStyler)) {
          await geostylerData(layerConf, olLayer)
        }

        // Setting the source will cause the data to be loaded
        olLayer.setSource(geojsonSource)

        // Listen to future changes in layer visibility and broadcast through Model()
        PoziEvents().onLayerVisible(olLayer)

        olLayer.set('initialised', true)

        console.debug('PoziConfigGeoJSON - onLayerFirstTimeVisible - initialised', titleid, layerConf, olLayer, geojsonSource)
      } else {
        console.warn('PoziConfigGeoJSON - Layer not visible yet, retrying next time. We should never have gotten here.', titleid)

        // Listen once more for visibility change. Should now be visible
        olLayer.once('change:visible', onLayerFirstTimeVisible)
      }
    }

    // Initialise layer when visible for first time
    olLayer.once('change:visible', onLayerFirstTimeVisible)

    // Set layer's visibility as determined by the config
    // NOTE: DISABLED. This now happens in map-base after the layers have been
    //       added to the map to avoid issues where the layer has not been registered
    //       yet in the redux store
    // const layerIsSelected = PoziUtils.isLayerSelected(layerConf, true)
    // olLayer.setVisible(layerIsSelected)

    // Store config object in layer
    olLayer.set('legacyStyleOptions', configObj)

    return olLayer
  }

  /**
    *
    * The writeStyle implementation of the GeoStyler-Style StyleParser interface.
    * It reads a GeoStyler-Style Style and returns a Promise.
    * The Promise itself resolves one of three types
    *
    * 1. OlStyle if input Style consists of
    *    one rule with one symbolizer, no filter, no scaleDenominator, no TextSymbolizer
    * 2. OlStyle[] if input Style consists of
    *    one rule with multiple symbolizers, no filter, no scaleDenominator, no TextSymbolizer
    * 3. OlParserStyleFct for everything else
    *
    **/
  var geostylerData = async function (layerConf, olLayer) {
    // console.debug('geostylerData', arguments)

    if (!layerConf) {
      console.warn('geostylerData - Error: no layer conf provided')
      return
    }

    if (!olLayer) {
      console.warn('geostylerData - Error: no OL layer provided', olLayer)
      return
    }

    const url = get(layerConf, 'styleGeoStyler.url', null)
    if (!url) {
      console.warn('geostylerData - Error: no url provided for', layerConf.titleid)
      return
    }

    const type = get(layerConf, 'styleGeoStyler.type', '')
    const titleid = get(layerConf, 'titleid', '')

    const OpenLayersParser = (await import('geostyler-openlayers-parser')).default

    let InputParser

    switch (type.toLowerCase()) {
      case 'qml':
        InputParser = (await import('geostyler-qgis-parser')).default
        break
      case 'sld':
        InputParser = (await import('geostyler-sld-parser')).default
        break
      case 'geostyler':
        break
      default:
        console.warn('Could not find a matching geostyler parser for type:', type, titleid)
        return
    }

    const inputParser = InputParser && new InputParser()

    if (InputParser && !inputParser) {
      console.warn('Could not find a matching geostyler parser for type:', type, titleid)
      return false
    }

    const outputParser = new OpenLayersParser()

    let olStyleDefault = null
    const layerStyle = {}
    let geostylerStyle

    if (url) {
      // console.debug('geostyler style URL', type, titleid, url)
      const ajaxOptions = {
        url,
        dataType: 'text'
      }

      let responseText = null

      // Show loading spinner.
      // In 'LoaderHandleGeoJSON' we will set layer load done/failed
      window.store.dispatch(setLayerLoadInProgress(titleid))

      try {
        responseText = await PoziAPIData().ajax(ajaxOptions)
      } catch (error) {
        console.warn('Failed to fetch url for default geostyler style', type, titleid, url, error)
      } finally {
        // Hide loading spinner.
        window.store.dispatch(setLayerLoadDone(titleid))
      }

      // console.debug('GeoJSON GeoStyler - responseText', responseText)

      if (responseText) {
        let ruleWithElseFilterIndex = -1

        try {
          // Work around limitation in GeoStyler where SLD version does not get detected automatically
          // by the current geostyler SLD parser (v3.0.0)
          if (type.toLowerCase() === 'sld') {
            // Use fast-xml-parser for getting version info
            const parserOptions = {
              attributeNamePrefix: '',
              ignoreAttributes: false,
              removeNSPrefix: true
            }
            const xmlParser = new XMLParser(parserOptions)
            const sldObject = xmlParser.parse(responseText)

            // console.debug('GeoJSON Geostyler sldObject', titleid, sldObject)

            const sldVersion = sldObject?.StyledLayerDescriptor?.version
            // console.debug('GeoJSON GeoStyler - sld version from document', sldVersion)

            inputParser._sldVersion = sldVersion || '1.1.0' // default to version 1.1.0 unless specified in document

            // Remove <ElseFilter /> rules as they are not supported by GeoStyler
            const rules = sldObject?.StyledLayerDescriptor?.NamedLayer?.UserStyle?.FeatureTypeStyle?.Rule

            // console.debug('GeoJSON GeoStyler - sld rules', titleid, rules)

            if (rules && rules.length > 0) {
              const ruleIndex = rules.findIndex(r => Object.keys(r).includes('ElseFilter'))
              if (ruleIndex > -1) {
                ruleWithElseFilterIndex = ruleIndex

                console.debug('GeoJSON GeoStyler - found unsupported ElseFilter in rules. Removing.', titleid, ruleIndex, rules[ruleIndex])
              }
            }
          }

          if (inputParser) {
            try {
              const parsedResult = await inputParser.readStyle(responseText)

              // The SLD parser does not always throw an error when something goes wrong but stores
              // error information in the result.errors.
              if (parsedResult?.errors) {
                console.warn('GeoJSON GeoStyler - style parsing error', url, parsedResult.errors)
              }

              // Set the style
              const output = parsedResult?.output
              if (output) {
                if (output.rules) {
                  // It can happen that there are no rules in the style. We then apply the default style
                  if (output.rules.length === 0) {
                    console.debug('GeoJSON GeoStyler - empty rules set found in style. Applying default style.', parsedResult)
                    return false
                  }

                  // If we have an ElseFilter rule, remove it (but not if it's the only rule)
                  if (ruleWithElseFilterIndex > -1 && output.rules.length !== 1) {
                    output.rules.splice(ruleWithElseFilterIndex, 1)
                  }
                }

                geostylerStyle = output

                // console.debug('GeoJSON GeoStyler - style', titleid, geostylerStyle)
              }
            } catch (error) {
            }
          } else {
            try {
              geostylerStyle = JSON.parse(responseText)
            } catch (error) {
              console.warn('GeoJSON GeoStyler - JSON parse error', error, responseText)
            }
          }
          // console.debug('GeoJSON GeoStyler - style', inputParser, geostylerStyle)
        } catch (error) {
          console.warn('GeoJSON GeoStyler - Error parsing style response', titleid, responseText, inputParser, error)
        }
      }
    }

    if (!geostylerStyle) {
      console.warn('GeoJSON GeoStyler - style failed for type', type, titleid, url, geostylerStyle)
      return false
    }

    try {
      // console.debug('GeoJSON GeoStyler - style', titleid, geostylerStyle)

      const { rules = [] } = geostylerStyle
      for (const rule of rules) {
        const { symbolizers = [] } = rule
        // console.debug('Geostyler - symbolizers', titleid, symbolizers)

        for (const symbolizer of symbolizers) {
          // console.debug('Geostyler - symbolizer', titleid, symbolizer)

          if (symbolizer?.kind === 'Text') {
            // Work around bug in GeoStyler where the stroke and fill colour get ignored
            // See: https://github.com/geostyler/geostyler-sld-parser/issues/524
            // There is also an as of yet unreported issue where the stroke-width of the
            // Polygon Symbolizer gets used by the Text Symbolizer
            // TODO: Remove this when issue has been resolved
            if (!symbolizer.stroke && symbolizer.color === '#000000') {
              if (typeof symbolizer.haloWidth === 'undefined') {
                symbolizer.haloWidth = 1
              }
              if (typeof symbolizer.haloColor === 'undefined') {
                symbolizer.haloColor = '#ffffff'
              }
            }
          }

          // Either QGIS or geostyler-sld-parser swaps the vertical direction, so we'll swap back here
          // TODO: figure out where the problem lies and report/fix it.
          if (symbolizer.offset) {
            const [x, y] = symbolizer.offset
            symbolizer.offset = [x, -y]
          }

          if (symbolizer.kind === 'Icon') {
            try {
              // console.debug('Geostyler - image original URL', titleid, symbolizer.image)

              let imageUrl = symbolizer.image

              if (!imageUrl.startsWith('https://')) {
                // Replace any URLs that point to the local file system with their equivalent in the cloud
                // Example: 'C:/OSGeo4W/apps/qgis-ltr/svg/sport/...' becomes 'qgis-ltr/sport/...'
                imageUrl = imageUrl.replace(/^(.*qgis(-ltr)?\/svg\/)/, 'qgis-ltr/')
              }

              // If the image is a relative one (has no 'https://') then we automatically
              // prepend it with the base url below. This is where we host our own SVG files from.
              const imageBaseUrl = 'https://connect.pozi.com/svg/'
              const imageUrlObj = new URL(imageUrl, imageBaseUrl)

              // Add custom property to enable the icon to be updated for the legend
              if (imageUrl.includes('pozi-bubble-2.0.svg')) {
                rule.isPoziBubble = true
                rule.poziBubbleFill = imageUrlObj.searchParams.get('fill')
              }

              // console.debug('Geostyler - image URL', titleid, imageUrlObj.href)

              // If the image is a special SVG that allows QGIS to change the styling of, then
              // use the search params to apply the styles to the SVG to get the same styling.
              const imageIsSvg = imageUrlObj.pathname.endsWith('svg')
              const imageHasSearchParams = imageUrlObj?.searchParams?.size > 0
              if (imageIsSvg && imageHasSearchParams) {
                let svgContent = await PoziAPIData().ajax({
                  dataType: 'xml',
                  url: imageUrlObj.href
                })

                // console.debug('GeoStyler SVG Node', titleid, svgContent.firstElementChild)

                // Explicitly set the dimensions of the SVG if not stored inside the SVG.
                // If we don't do this, the SVG may not display correctly.
                const svgNode = svgContent.firstElementChild
                const svgHeight = svgNode?.getAttribute('height')
                const svgWidth = svgNode?.getAttribute('width')
                if (svgNode && symbolizer.size && !(svgHeight && svgWidth)) {
                  // Try to get the dimensions from the viewBox attribute, if defined
                  const svgViewBox = svgNode?.getAttribute('viewBox')
                  let height, width
                  if (svgViewBox) {
                    const [_minX, _minY, vbWidth, vbHeight] = svgViewBox.split(' ')
                    height = vbHeight
                    width = vbWidth

                    // console.debug('GeoStyler - viewBox', titleid, svgViewBox)
                  } else {
                    // Last resort fallback. This assumes that the SVG is square, which is not guaranteed
                    // of course.
                    height = symbolizer.size
                    width = symbolizer.size
                  }

                  // Set the dimensions in pixels
                  svgNode.setAttribute('height', `${height}px`)
                  svgNode.setAttribute('width', `${width}px`)
                }

                // Get the SVG as text out of the node
                svgContent = svgNode?.outerHTML ?? ''

                // console.debug('Geostyler - SVG content', titleid, svgContent)

                // Get search searchParams from URL object and turn them into an object
                const searchParams = Object.fromEntries(imageUrlObj.searchParams)

                // Match strings like: fill="param(fill) #ff0000" and replace everything between the
                // double quotes with the value of `fill` from the searchParams or the default value
                // of `#ff0000` in this case.
                svgContent = svgContent.replace(/"param\(([a-z-]+)\) ?([#a-z0-9]*)?"/g,
                  (fullMatch, match, fallbackValue) => {
                    // console.debug('geostyler SVG replace', match, fallbackValue, fullMatch)

                    const replacementVal = searchParams[match] ?? fallbackValue
                    const replacementStr = `"${replacementVal}"`
                    return replacementStr
                  }
                )

                // console.debug('Geostyler - image SVG with params replaced', titleid, svgContent)

                // Store the modified SVG inline, encoded as Base64
                symbolizer.image = `data:image/svg+xml;base64,${window.btoa(svgContent)}`
              }
            } catch (error) {
              console.warn('Geostyler - unexpected error', titleid, error, symbolizer.image)
            }
          }
        }
      }

      // Convert GeoStyler style to OL style
      const olStyleGeoStyler = (await outputParser.writeStyle(geostylerStyle)).output

      // Workaround for geostyler-openlayers-parser not yet supporting
      // point symbolisers for line/polygon styles at the time of writing (9/8/2023)
      //
      // See: https://github.com/geostyler/geostyler-openlayers-parser/pull/245

      // Handle both singular and composite styles
      for (const olStyle of [].concat(olStyleGeoStyler)) {
        // Only apply a geometry function if the style is a point style (i.e. has an image)
        const styleHasImageFn = typeof olStyle.getImage === 'function'
        const styleHasImage = styleHasImageFn && !!olStyle?.getImage()

        if (!styleHasImage) {
          continue
        }

        // Set/override geometry function
        olStyle.geometryFunction_ = (feature) => {
          const geometry = feature.getGeometry()
          const geomHasInteriorPoint = typeof geometry?.getInteriorPoint === 'function'

          // console.debug('Geostyler - geometryFunction_', titleid, geomHasInteriorPoint, geometry)

          return geomHasInteriorPoint ? geometry.getInteriorPoint() : geometry
        }
      }

      // console.debug('Geostyler - OL style', titleid, olStyleGeoStyler)

      // Modify the generated style to make label placements follow lines
      olStyleDefault = (feature, resolution) => {
        if (!olStyleGeoStyler) {
          return
        }

        // If the old style is a function, then call that to return the actual
        // styles, otherwise just add it to the styles array
        let newStyles = []
        if (typeof olStyleGeoStyler === 'function') {
          const oldStylesArray = olStyleGeoStyler(feature, resolution)
          if (oldStylesArray) {
            newStyles = newStyles.concat(oldStylesArray)
          }
        } else {
          newStyles = newStyles.concat(olStyleGeoStyler)
        }

        // If a feature is of type LineString or MultiLineString, loop
        // through every style make the text placement to follow the line
        const geom = feature.getGeometry && feature.getGeometry()
        const geomType = (geom.getType && geom.getType()) || ''
        const geomTypeIsLine = !!geomType.match(/curve|line/i)
        if (geomTypeIsLine) {
          newStyles.forEach(style => {
            if (style.getText) {
              const textStyle = style.getText()
              if (textStyle?.setPlacement) {
                textStyle.setPlacement('line')
              }
            }
          })
        }

        return newStyles
      }
    } catch (error) {
      console.warn('Error creating style', type, titleid, geostylerStyle, error)
    }

    // console.debug('GeoStyler style', type, titleid, geostylerStyle, olStyleDefault)

    // Store geostyler style
    layerStyle.geostyler = geostylerStyle
    if (app.layerConfig[layerConf.title]) {
      app.layerConfig[layerConf.title].style = layerStyle
    } else {
      console.log(`GeoStyler style - cannot find layerConfig for '${layerConf.titleid}'. Cannot store style!`)
    }

    // Store GeoStyler style in redux
    // We delay this call a bit since due to timing issues, this could
    // be called before the layer is registered in redux.
    window.setTimeout(() => {
      window.store.dispatch(setLayerStyleGeoStyler(titleid, geostylerStyle))
    }, 200)

    // Set as default style for OL layer
    olLayer.setStyle(olStyleDefault)

    PoziMap().unselectAllFeaturesStylingOnly(olLayer)

    const state = window.store.getState()
    const currentTarget = get(state, 'currentTarget', null)
    const setStyleOnly = true
    const zoomFunction = null
    if (layerConf.selected) {
      const extraOptions = {
        setStyleOnly,
        zoomFunction
      }

      // If layer turned on then set the new styling and select the target feature.
      PoziUtils.selectVectorFeatureFromIDTextAndLayerConfig(currentTarget?.propertiesId, layerConf.titleid, extraOptions)
    }
  }

  var setPointStyleFunctionForPolygonClass = function (configObj, layer) {
    //
    // Handle Polygon Style Classes
    //
    const arrStyleUnselectedOptions = []
    const selectedStrokeRGBA = Model.get('config.layout.map.selection.stroke.color', 'rgba(255, 140, 0, 0.5)')
    const selectedFillRGBA = Model.get('config.layout.map.selection.fill.color', 'rgba(255, 140, 0, 0.1)')

    const defaultStyleSelectedStroke = {
      color: selectedStrokeRGBA,
      width: 6
    }
    const defaultStyleSelectedFill = {
      color: selectedFillRGBA
    }

    const strokeStyleSelected = get(layer, 'styleClassPolygon.selectedStyle.style.polygon.stroke', defaultStyleSelectedStroke)
    const fillStyleSelected = get(layer, 'styleClassPolygon.selectedStyle.style.polygon.fill', defaultStyleSelectedFill)
    const objStyleSelected = {
      stroke: new OLStyleStroke(strokeStyleSelected),
      fill: new OLStyleFill(fillStyleSelected)
    }

    const defaultStyleUnselectedStroke = {
      color: '#EE82EE',
      width: 3
    }
    const defaultStyleUnselectedFill = {
      color: 'grey'
    }

    const strokeStyleUnselected = get(layer, 'styleClassPolygon.unSelectedStyle.style.polygon.stroke', defaultStyleUnselectedStroke)
    const fillStyleUnselected = get(layer, 'styleClassPolygon.unSelectedStyle.style.polygon.fill', defaultStyleUnselectedFill)
    const objStyleUnselected = {
      stroke: new OLStyleStroke(strokeStyleUnselected),
      fill: new OLStyleFill(fillStyleUnselected)
    }

    $.each(layer.styleClassPolygon.values, function (key, val) {
      arrStyleUnselectedOptions[key] = {
        stroke: new OLStyleStroke(layer.styleClassPolygon.values[key].style.polygon.stroke),
        fill: new OLStyleFill(layer.styleClassPolygon.values[key].style.polygon.fill)
      }
    })

    const funcUnselectedStyle = function (feature, resolution) {
      const featVal = PoziUtils.getFeaturePropertyValueCaseInsensitive(feature, layer.styleClassPolygon.property)
      const style = arrStyleUnselectedOptions[featVal]
      if (featVal) {
        // TODO: Should this be if style?
        return [new OLStyleStyle(style)]
      } else {
        return [new OLStyleStyle(objStyleUnselected)]
      }
    }

    const funcSelectedStyle = function (feature, resolution) {
      return [new OLStyleStyle(objStyleSelected)]
    }

    // Defines the style of the Polygons (despite the name)
    // configObj.defaultStyleStack = defaultStyleStack;
    configObj.pointStyle = funcUnselectedStyle
    configObj.pointSelectedStyle = funcSelectedStyle
  }

  //
  // Handle other style types
  //
  var setPointStyleFunctionForOtherStyleTypes = function (configObj, layer) {
    configObj.shadow = new OLStyleStyle({
      image: new OLStyleIcon({
        crossOrigin: 'Anonymous',
        scale: 0.85,
        anchor: [30, 10],
        anchorXUnits: 'pixels',
        anchorYUnits: 'pixels',
        opacity: 0.4,
        src: 'img/bubble-shadow-ellipse.png'
      }),
      zIndex: 1
    })

    const backgroundBubbleObj = {
      image: new OLStyleIcon({
        crossOrigin: 'Anonymous',
        size: [100, 100],
        scale: 0.5,
        anchor: [50, 90],
        anchorXUnits: 'pixels',
        anchorYUnits: 'pixels',
        opacity: 1,
        src: getBubbleBackgroundPNGPath(layer.style, layer.title)
      })
    }
    // 2 styles based on the same object: 1 for default rendering, 1 for highlighted rendering
    // These are required to cater for overlapping features
    configObj.backgroundBubble1 = new OLStyleStyle(
      $.extend(
        {
          zIndex: 3
        },
        backgroundBubbleObj
      )
    )
    configObj.backgroundBubble2 = new OLStyleStyle(
      $.extend(
        {
          zIndex: 53
        },
        backgroundBubbleObj
      )
    )

    // If single style
    let defaultStyleStack

    if (layer.style) {
      const styleNotIcon = layer.style.polygon || layer.style.linestring || layer.style.point
      if (styleNotIcon) {
        let styleForGeometryTypeObj
        if (styleNotIcon.radius) {
          if (styleNotIcon.points) {
            // Regular shape style
            styleForGeometryTypeObj = {
              image: new OLStyleRegularShape({
                crossOrigin: 'Anonymous',
                radius: styleNotIcon.radius || 1,
                stroke: new OLStyleStroke(styleNotIcon.stroke || {}),
                fill: new OLStyleFill(styleNotIcon.fill || {}),
                points: styleNotIcon.points,
                angle: styleNotIcon.angle || Math.PI / 4
              })
            }
          } else {
            // Circle style
            styleForGeometryTypeObj = {
              image: new OLStyleCircle({
                crossOrigin: 'Anonymous',
                radius: styleNotIcon.radius || 1,
                stroke: new OLStyleStroke(styleNotIcon.stroke || {}),
                fill: new OLStyleFill(styleNotIcon.fill || {})
              })
            }
          }
        } else {
          styleForGeometryTypeObj = {
            stroke: new OLStyleStroke(styleNotIcon.stroke || {}),
            fill: new OLStyleFill(styleNotIcon.fill || {})
          }
        }
        configObj.pointStyle = function (feature, resolution) {
          // var styleForGeometryType;
          if (styleNotIcon && styleNotIcon.text) {
            const getText = function (feature, resolution) {
              if (styleNotIcon.text.maxResolution && styleNotIcon.text.maxResolution < resolution) {
                return ''
              }
              let t = PoziUtils.getFeaturePropertyValueCaseInsensitive(feature, styleNotIcon.text.attribute)
              // Wrapping text on a character set
              // Note: line retruns are not implemented in ol3 text styles yet, so it won't work as of today (3.8.2)
              if (styleNotIcon.text.wrapOn) {
                t = PoziUtils.replaceAll(t, styleNotIcon.text.wrapOn, '\n')
              }
              return t
            }
            styleForGeometryTypeObj.text = new OLStyleText({
              crossOrigin: 'Anonymous',
              text: getText(feature, resolution),
              font: styleNotIcon.font || 'Bold 11px Arial',
              fill: new OLStyleFill(styleNotIcon.text.fill),
              stroke: new OLStyleStroke({
                color: 'white',
                width: 3
              })
            })
          }
          return [new OLStyleStyle(styleForGeometryTypeObj)]
        }
      } else {
        // Adding the anchorage information based on the declared image size (or 32 if not declared)
        const decHeight = layer.style.size ? layer.style.size[1] : 28
        const decScale = layer.style.scale || 1
        const anchorObj = {
          anchor: [0.5, (layer.style.noBubbleContainer ? -4 / decScale : 8 / decScale) + (decHeight + 32 / decScale) / 2],
          anchorXUnits: 'fraction',
          anchorYUnits: 'pixels'
        }
        layer.style = $.extend(layer.style, anchorObj)
        layer.style.crossOrigin = 'Anonymous'

        // 2 styles: 1 for default rendering, 1 for highlighted (elevated to avoid overlapping features issues)
        configObj.style1 = new OLStyleStyle({
          image: new OLStyleIcon(layer.style),
          crossOrigin: 'Anonymous',
          zIndex: 3
        })
        configObj.style2 = new OLStyleStyle({
          image: new OLStyleIcon(layer.style),
          crossOrigin: 'Anonymous',
          zIndex: 53
        })

        defaultStyleStack = [configObj.shadow, configObj.backgroundBubble1, configObj.style1]

        // Style stack is fixed
        configObj.pointStyle = function (feature, resolution) {
          return defaultStyleStack
        }
      }
    }

    // If multiple styles (classes of value)
    if (layer.styleClass) {
      configObj.style1 = {}
      configObj.style2 = {}
      configObj.backgroundBubble1 = {}
      configObj.backgroundBubble2 = {}
      // Looping through all possible values of the property, to define all styles
      $.each(layer.styleClass.values, function (key, val) {
        const featureKey = key.toLowerCase().trim()

        // Adding the anchorage information based on the declared image size (or 32 if not declared)
        const decHeight = val.size ? val.size[1] : 28
        const decScale = val.scale || 1
        const anchorObj = {
          anchor: [0.5, (val.noBubbleContainer ? -4 / decScale : 8 / decScale) + (decHeight + 32 / decScale) / 2],
          anchorXUnits: 'fraction',
          anchorYUnits: 'pixels'
        }
        $.extend(val, anchorObj)
        val.crossOrigin = 'Anonymous'

        // 2 style arrays: 1 for default rendering, 1 for highlighted (elevated to avoid overlapping features issues)
        configObj.style1[featureKey] = new OLStyleStyle({
          image: new OLStyleIcon(val),
          crossOrigin: 'Anonymous',
          zIndex: 3
        })
        configObj.style2[featureKey] = new OLStyleStyle({
          image: new OLStyleIcon(val),
          crossOrigin: 'Anonymous',
          zIndex: 53
        })

        const title = layer.title + ' - ' + key
        const backgroundBubbleObj = {
          image: new OLStyleIcon({
            crossOrigin: 'Anonymous',
            size: [100, 100],
            scale: 0.5,
            anchor: [50, 90],
            anchorXUnits: 'pixels',
            anchorYUnits: 'pixels',
            opacity: 1,
            src: getBubbleBackgroundPNGPath(val, title)
          })
        }

        // 2 styles based on the same object: 1 for default rendering, 1 for highlighted rendering
        // These are required to cater for overlapping features
        configObj.backgroundBubble1[featureKey] = new OLStyleStyle(
          $.extend(
            {
              zIndex: 3
            },
            backgroundBubbleObj
          )
        )
        configObj.backgroundBubble2[featureKey] = new OLStyleStyle(
          $.extend(
            {
              zIndex: 53
            },
            backgroundBubbleObj
          )
        )
      })
      // Style function caters for property dependent styles
      configObj.pointStyle = function (feature, resolution) {
        const featureKey = PoziUtils.getFeatureKey(feature, layer.styleClass.property)
        if (featureKey) {
          if (configObj.style1[featureKey]) {
            // console.log('Found style for feature', featureKey)
            return [configObj.shadow, configObj.backgroundBubble1[featureKey], configObj.style1[featureKey]]
          } else {
            const msg = 'Style class configuration not defined for featureid ' + featureKey + ' on layer ' + layer.titleid
            console.log(msg)
            PoziUtils.gtag({
              category: 'config',
              status: 'warning',
              identifier: layer.titleid + '-' + featureKey,
              action: 'Getting styleclass for feature',
              additionalInfo: msg
            })
          }
        }
      }
    }

    // configObj.defaultStyleStack = defaultStyleStack;
  }

  /**
   * @returns Path to a prebuilt png file based on an rgb color hash.
   * @param color #000000
   */
  var getBubbleBackgroundPNGPath = function (style, layername) {
    const defaultBB = './img/bubble/png-100x100/bubble-C0C0C0.png'

    if (style && !style.bubbleBackground) {
      // console.warn('Returning default Bubble Background for ' + layername)
      return defaultBB
    }

    if (!style || !style.bubbleBackground || style.noBubbleContainer) {
      // console.log("Returning blank Bubble Background for " + layername)
      return app.blank
    }
    const rgbcolor = style.bubbleBackground
    if (rgbcolor.indexOf('#') === -1) {
      console.log('Invalid RGB color... must start with #')
      return defaultBB
    }
    const bb = './img/bubble/png-100x100/bubble-' + rgbcolor.replace('#', '') + '.png'
    // console.log("Returning Bubble Background for " + layername + " " + bb)
    return bb
  }

  // Return adapters (must be at end of adapter)
  return adapters
})

export default PoziConfigGeoJSON

// End A (Adapter)
